import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * Live, interactive preview of the *real* public signup, embedded via an
 * iframe. The venue's draft branding is pushed in over postMessage so edits
 * show instantly without saving; a device toggle scales the iframe to fit.
 *
 * Shared by the admin (AdminVenueDetail) and client (settings/Branding) editors
 * so the preview can never drift from the actual signup, or from each other.
 *
 *   venue: the draft venue object being edited (all branding fields)
 *   slug:  venue slug — the signup URL the iframe loads (…/:slug?brandPreview=1)
 */
export default function SignupLivePreview({ venue, slug }) {
  const iframeRef = useRef(null);
  const wrapRef = useRef(null);
  const [device, setDevice] = useState("desktop");
  const [scale, setScale] = useState(1);
  const LOGICAL = device === "desktop" ? { w: 1200, h: 780 } : { w: 400, h: 760 };

  const post = useCallback(() => {
    const win = iframeRef.current && iframeRef.current.contentWindow;
    if (win) win.postMessage({ type: "suBrandPreview", venue }, "*");
  }, [venue]);

  // Debounced push of the current draft on any change.
  useEffect(() => { const t = setTimeout(post, 250); return () => clearTimeout(t); }, [post]);

  // Answer the iframe's ready handshake with the current draft.
  useEffect(() => {
    const onMsg = (e) => { if (e.data && e.data.type === "suBrandPreviewReady") post(); };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [post]);

  // Scale the fixed-size iframe to fit the available width.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => setScale(Math.min(1, el.clientWidth / LOGICAL.w)));
    ro.observe(el);
    return () => ro.disconnect();
  }, [LOGICAL.w]);

  if (!slug) return null;

  return (
    <div className="settings-card" style={{ padding: 0, overflow: "hidden" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.75rem 1.25rem", borderBottom: "1px solid var(--border)", gap: "0.75rem", flexWrap: "wrap" }}>
        <div><strong style={{ fontSize: "0.95rem" }}>Live preview</strong> <span className="muted" style={{ fontSize: "0.8rem" }}>— your real signup, interactive &amp; updating as you edit</span></div>
        <div style={{ display: "inline-flex", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", overflow: "hidden" }}>
          <button type="button" className={`btn btn-sm ${device === "desktop" ? "btn-primary" : "btn-ghost"}`} style={{ borderRadius: 0, border: "none" }} onClick={() => setDevice("desktop")}>Desktop</button>
          <button type="button" className={`btn btn-sm ${device === "mobile" ? "btn-primary" : "btn-ghost"}`} style={{ borderRadius: 0, border: "none" }} onClick={() => setDevice("mobile")}>Mobile</button>
        </div>
      </div>
      <div style={{ padding: "1rem", background: "#0b1020" }}>
        <div ref={wrapRef} style={{ width: "100%" }}>
          <div style={{ margin: "0 auto", width: LOGICAL.w * scale, height: LOGICAL.h * scale, position: "relative", overflow: "hidden", borderRadius: 12, boxShadow: "0 14px 44px rgba(0,0,0,0.45)" }}>
            <iframe
              ref={iframeRef}
              title="Signup preview"
              src={`/${slug}?brandPreview=1`}
              onLoad={post}
              style={{ border: 0, width: LOGICAL.w, height: LOGICAL.h, position: "absolute", top: 0, left: 0, transformOrigin: "top left", transform: `scale(${scale})`, background: "#0b1020" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
